'use client'

/**
 * LoginClient Component:
 * Handles user login functionality using formik, redux dispatch, and cookie/session handling.
 */

import { Button, TextInput } from '@allen-career-institute/allen-web-common'
import { loginSchema } from '../../../schemas/authSchema' // Yup validation schema for login
import { loginInitialValues } from '@school/schemas/initialValues' // Initial form values for login
import { useDispatch } from 'react-redux'
import { loginUser, createSession } from '@school/common/common-utils/slices/authSlice' // Redux thunks
import { AppDispatch } from '@school/store/store'
import { LoginPayload, User } from '@school/schemas/typeInterface'
import { useRouter, useSearchParams } from 'next/navigation' // Router hooks
import FormWrapper from '@school/common/common-utils/wrapper/form' // Form wrapper component
import useCustomFormik from '@school/common/common-utils/hooks/useCustomFormik' // Custom Formik wrapper
import { cookieClient } from '@school/common/common-utils/utils/cookieClient' // Utility to handle cookies
import { LoginHeading, LoginText } from '@school/constant/login' // UI Text constants
import { trimmedValues } from '@school/common/common-utils/utils/urlUtils'

const LoginClient = () => {
  const dispatch = useDispatch<AppDispatch>()
  const router = useRouter()
  const searchParams = useSearchParams()
  const userId = searchParams?.get('userId') // Optional query param to pre-set user ID
  const redirect = searchParams?.get('redirect') // Optional redirect after login

  /**
   * Handles redirection and session setup after login
   */
  const redirectUser = (user: User, token: string): void => {
    // If user is not required to update password, set session and cookies
    if (!user?.can_update_password) {
      dispatch(createSession({ user, token }))
      cookieClient.set('token', token)
      cookieClient.set('user', JSON.stringify(user))
    } else {
      // Otherwise, set special cookie for redirection to password reset
      cookieClient.set('_accessID', user?.id)
    }

    // Redirect user to reset-password or intended route or default '/school'
    const redirectLink = user?.can_update_password ? '/reset-password' : redirect || '/school'
    router.push(redirectLink)
  }

  // Initialize formik with schema, initial values, and submission handler
  const formik = useCustomFormik({
    initialValues: loginInitialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, formikHelpers) => {
      const { setErrors, setSubmitting } = formikHelpers
      // Append userId from query params if present
      if (userId?.trim()) values.id = userId
      else delete values.id
      //clean the data
      const cleanedValues: LoginPayload = trimmedValues(values)
      // Re-validate after trimming
      try {
        await loginSchema.validate(cleanedValues, { abortEarly: false })
      } catch (validationError: any) {
        const formattedErrors: Record<string, string> = {}
        validationError.inner.forEach((err: any) => {
          if (err.path) {
            formattedErrors[err.path] = err.message
          }
        })
        setErrors(formattedErrors)
        setSubmitting(false)
        return // Prevent submission if validation fails
      }
      // Dispatch login thunk
      const res = await dispatch(loginUser(cleanedValues))
      if (loginUser.fulfilled.match(res)) {
        const userData = res.payload
        redirectUser(userData, userData?.token)
      } else {
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      {/* Page Heading */}
      <h2 className='text-3xl md:text-4xl font-bold leading-normal'>{LoginHeading}</h2>
      <p className='text-grayShadeLight font-medium text-xl md:text-2xl leading-normal mb-10'>
        {LoginText}
      </p>

      {/* Form Start */}
      <FormWrapper onSubmit={formik.handleSubmit}>
        {/* Email Input Field */}
        <TextInput
          label='Email'
          placeholder='Enter your email'
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='email'
          inputStyleClass='!bg-white w-full'
          containerStyleClass='border-1 border-cardBottomBorderColor w-100 mb-2 bg-white'
          widthType='lg'
          type='email'
          autoComplete='email'
        />
        {/* Email validation error */}
        {formik.touched.email && formik.errors.email && (
          <div style={{ color: '#db0b00', fontSize: '14px', marginTop: '4px' }}>
            {formik.errors.email}
          </div>
        )}

        {/* Password Input Field */}
        <TextInput
          label='Password'
          placeholder='Enter your password'
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name='password'
          inputStyleClass='!bg-white w-full'
          containerStyleClass='border-1 border-cardBottomBorderColor w-100 mb-2 bg-white'
          widthType='lg'
          type='password'
          autoComplete='new-password'
        />
        {/* Password validation error */}
        {formik.touched.password && formik.errors.password && (
          <div className='text-[#db0b00] text-sm mt-1'>{formik.errors.password}</div>
        )}

        {/* Forgot Password Link */}
        <div className='text-right mt-2 mb-4'>
          <a
            href='/forgot-password'
            className='text-sm text-[#233A6C] hover:underline text-[14px] font-semibold'
          >
            Forgot Password?
          </a>
        </div>

        {/* Submit Button */}
        <Button
          type='submit'
          className='bg-[#233A6C] text-white hover:bg-[#4285f4] border-[#233A6C]  w-full justify-center flex mt-7'
        >
          Sign In
        </Button>
      </FormWrapper>
    </>
  )
}

export default LoginClient
